import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import { Box, CircularProgress } from "@mui/material";
import AmCharts from "../../chart/amcharts";
import { GRAPH_TYPE } from "../../../constants/trendGraph/trendGraphType";
import { createGeneratorData } from "../../comparison/hooks/useComparisonDataFetch";
import { useTranslation } from "react-i18next";
import localizationKeys from "../../../i18n/localizationKeys";
import { createCSV, createCsvDownload } from "../../../helpers/csvDownload/createCSV";
import { getCurrentFormattedDateTimeTextWithTimezoneForCSV } from "../../../util/timezone";
import { selectedTimezone } from "../../../model/slice/timezoneSlice";
import {
  fetchGeneratorEngineData,
  fetchShaftGeneratorEngineData,
} from "../../../api/baseEngineData";
import { calDurations } from "../../../constants/calDuration";

const onCSVButtonClickHandler = async ({ graphData, vesselName, chartName, timezone }) => {
  const blob = await createCSV(graphData, ",");
  const fileName = `${vesselName}_sep_${chartName}_${getCurrentFormattedDateTimeTextWithTimezoneForCSV(timezone)}`;
  createCsvDownload({ blob, filename: fileName });
};

export const ElectricTrendWidget = (props) => {
  const { vessel, selectedChart, index, startDate, endDate } = props;
  const { t } = useTranslation();
  const timezone = useSelector(selectedTimezone);
  const [graphData, setGraphData] = useState([]);
  const [geData, setGeData] = useState([]);
  const [sgData, setSgData] = useState([]);
  const [isFetching, setIsFetching] = useState(false);
  const [isDataProcessing, setIsDataProcessing] = useState(true);

  useEffect(() => {
    const fetchData = async ({ vesselId, startDate, endDate, interval }) => {
      const controller = new AbortController();
      const signal = controller.signal;
      setIsFetching(true);
      const [generatorEngineData, shaftGeneratorData] = await Promise.all([
        fetchGeneratorEngineData({
          vesselId,
          startDate,
          endDate,
          interval,
          signal,
        }),
        fetchShaftGeneratorEngineData({
          vesselId,
          startDate,
          endDate,
          interval,
          signal,
        }),
      ]);
      setGeData(generatorEngineData?.loadingData ?? []);
      setSgData(shaftGeneratorData?.loadingData ?? []);
      setIsFetching(false);
    };
    const interval = calDurations({ startDate, endDate });
    fetchData({ vesselId: vessel.id, startDate, endDate, interval });
  }, [startDate, endDate]);
  useEffect(() => {
    if (isFetching) return;
    createGeneratorData({
      sgData,
      geData,
      setGraphData,
      setIsLoading: setIsDataProcessing,
    });
  }, [isFetching]);
  return (
    <Box sx={{ height: "100%", padding: "8px 16px" }}>
      <h2 style={{ color: "white" }}>{t(localizationKeys.ElectricTrendGraph_upper)}</h2>
      <button
        name="csvDownloadButton"
        style={{ visibility: "hidden" }}
        onClick={() => {
          onCSVButtonClickHandler({
            graphData,
            vesselName: vessel.name,
            chartName: t(localizationKeys.ElectricTrendGraph_upper),
            timezone,
          });
        }}
      />
      <Box sx={{ height: "calc(100% - 30px)", minHeight: "100px" }}>
        {isFetching || isDataProcessing ? (
          <CircularProgress />
        ) : (
          <AmCharts
            graphData={graphData}
            graphDataDigital={[]}
            vesselId={vessel.id}
            chartType={selectedChart}
            type={GRAPH_TYPE.ELECTRIC}
            isComparison={false}
            vesselData={vessel}
            isMockPreview={false}
            expand={true}
            multipleGraphIndex={index}
            isVesselOverview={false}
            isDashboard={true}
            overviewTrendIdx={0}
          />
        )}
      </Box>
    </Box>
  );
};

ElectricTrendWidget.propTypes = {
  vessel: PropTypes.object,
  selectedChart: PropTypes.string,
  index: PropTypes.number,
  startDate: PropTypes.object,
  endDate: PropTypes.object,
};
